import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import SafeWarpShader from './SafeWarpShader';
import MainApp from './MainApp';
import SignInDemo from './SignInDemo';
import SellerPage from './SellerPage';
import ProductsPage from './ProductsPage';
import CustomerPage from './CustomerPage';
import CartPage from './CartPage';
import AISpaceCreator from './AISpaceCreator';
import AboutPage from './AboutPage';
import DualityLanding from './components/DualityLanding';
import { setCookie, deleteCookie } from './utils/cookies';
import { CartProvider } from './contexts/CartContext';
import { Analytics } from '@vercel/analytics/react';

function AppContent() {
  const [appLoaded, setAppLoaded] = useState(() => {
    // Check if app has already been loaded this session
    return sessionStorage.getItem('appLoaded') === 'true';
  });
  const [userType, setUserType] = useState<'seller' | 'customer' | null>(() => {
    // Only check sessionStorage (not cookies) on initial load
    // This ensures DualityLanding always shows on first page load
    const sessionValue = sessionStorage.getItem('userType') as 'seller' | 'customer' | null;
    return sessionValue;
  });
  const [pageTransitioning, setPageTransitioning] = useState(false);
  const [shouldResetUserType, setShouldResetUserType] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Skip loading screen for sign-in and seller pages
  const isSignInPage = location.pathname === '/signin' || location.pathname === '/seller';
  const isHomePage = location.pathname === '/';

  // Show main app after loading completes (only for main app and only once per session)
  useEffect(() => {
    // If already loaded this session, don't show loading screen again
    if (sessionStorage.getItem('appLoaded') === 'true') {
      setAppLoaded(true);
      return;
    }

    if (!isSignInPage) {
      const timer = setTimeout(() => {
        setAppLoaded(true);
        sessionStorage.setItem('appLoaded', 'true');
      }, 4000); // Show loading for 4 seconds

      return () => clearTimeout(timer);
    } else {
      setAppLoaded(true); // Immediately set as loaded for sign-in page
      sessionStorage.setItem('appLoaded', 'true');
    }
  }, [isSignInPage]);

  // Handle when user clicks Home - reset userType to force selection
  useEffect(() => {
    if (shouldResetUserType && isHomePage) {
      setUserType(null);
      sessionStorage.removeItem('userType');
      deleteCookie('userType'); // Also remove cookie
      setShouldResetUserType(false);
    }
  }, [shouldResetUserType, isHomePage]);

  // Expose reset function to be called from navbar
  useEffect(() => {
    const handleHomeClick = () => {
      setShouldResetUserType(true);
      navigate('/');
    };

    window.addEventListener('homeNavigationRequested', handleHomeClick);
    return () => window.removeEventListener('homeNavigationRequested', handleHomeClick);
  }, [navigate]);

  const handleUserTypeSelect = (type: 'seller' | 'customer') => {
    setUserType(type);
    sessionStorage.setItem('userType', type);
    setCookie('userType', type); // Also save to cookie
    setPageTransitioning(true);
  };

  if (!appLoaded && !isSignInPage) {
    return <SafeWarpShader />;
  }

  // Show duality landing only on home page and if user hasn't selected type yet
  if (isHomePage && !userType) {
    return (
      <DualityLanding
        onSellerSelect={() => handleUserTypeSelect('seller')}
        onCustomerSelect={() => handleUserTypeSelect('customer')}
      />
    );
  }

  return (
    <div className={pageTransitioning ? 'page-slide-up' : ''}>
      <Routes>
        <Route path="/" element={userType === 'seller' ? <MainApp /> : <CustomerPage />} />
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/create-space" element={<AISpaceCreator />} />
        <Route path="/signin" element={<SignInDemo />} />
        <Route path="/seller" element={<SellerPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <CartProvider>
      <Router>
        <AppContent />
      </Router>
      <Analytics />
    </CartProvider>
  );
}

export default App;